import React, { useEffect, FC, ReactElement, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { Alert, Space, Typography, Button } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleFilled, InfoCircleFilled } from '@ant-design/icons'
import ErrorsTable from './ErrorsTable'
import { Requests, apiCall } from '../../../api'
import { QC_REPORTS } from '../../../constants/QC_REPORTS'
import { Snapdocs } from '../../../hooks/api'
import { ChannelEvents } from '../../../models/event/channelEvents'
import { store } from '../../../store'
import { TitleQCReportProps, NotaryAcknowledgement } from './types'
import { PusherWrapper } from '../../../utils/pusherProvider'
import NotaryAcknowledgementAlerts from './NotaryAcknowledgementAlerts'
import NotaryAcknowledgementActions from './NotaryAcknowledgementActions'

const DESCRIPTIONS = {
  IN_PROGRESS: 'Snapdocs has received scanbacks from the notary and is the process of reviewing those documents.',
  COMPLETE: 'Snapdocs has reviewed the signed documents and produced the quality control report below.',
  PASSED: 'No errors were detected.',
  ERRORS: {
    agent: 'The settlement company or signing service may reach out to resolve issues.',
    client: 'Please work with the notary to resolve any outstanding issues specified below.'
  },
  NOTE_TO_TITLE: 'Please review the note from the Snapdocs review team. This note is only visible to you.'
}

const STATUS_ICONS = {
  [QC_REPORTS.STATUSES.IN_PROGRESS]: {
    icon: ClockCircleOutlined,
    color: 'color-ant-blue-4',
    label: 'Quality control check - in progress'
  },
  [QC_REPORTS.STATUSES.PASSED]: {
    icon: CheckCircleOutlined,
    color: 'color-ant-green-4',
    label: 'Quality control passed - no errors found'
  }
}

export const TitleQCReport: FC<TitleQCReportProps> = ({ orderId, role, shouldDisplayQCLink }): ReactElement | null => {
  const { response: qcReport } = Snapdocs.useResources(Requests.titleQCReport({ orderId }))
  const {
    completedAt,
    documentErrors,
    features,
    notaryAcknowledgement,
    noteToTitle,
    packageError,
    pusherConfig,
    reviewUrl,
    status,
    statusReason
  } = qcReport || {}

  const [submittingAck, setSubmittingAck] = useState(false)

  const submitAcknowledgement = async (ack: NotaryAcknowledgement) => {
    setSubmittingAck(true)
    try {
      const request = Requests.titleQCReportAcknowledge({ orderId, acknowledgement: ack })
      await apiCall.post(request.path, request.data)
      Snapdocs.fetchResources(Requests.titleQCReport({ orderId }), true)
    } finally {
      setSubmittingAck(false)
    }
  }

  useEffect(() => {
    if (!pusherConfig) return

    const { pusherAppKey, pusherAppCluster, qcReportsChannel } = pusherConfig
    const pusher = new PusherWrapper(pusherAppKey, pusherAppCluster)

    pusher.subscribeForEvent(qcReportsChannel, ChannelEvents.QcReportUpdated, () => {
      Snapdocs.fetchResources(Requests.titleQCReport({ orderId }), true)
    })

    return () => pusher.unsubscribeFromTheChannel(qcReportsChannel)
  }, [pusherConfig])

  const inProgress = status === QC_REPORTS.STATUSES.IN_PROGRESS
  const errors = status === QC_REPORTS.STATUSES.ERRORS

  const displayPackageError = Boolean(packageError)
  const displayNoteToTitle = role === 'admin' && Boolean(noteToTitle)
  const notaryAcknowledgementEnabled = role === 'agent' && errors && features?.includes('tqc_notary_sms')

  let description
  switch (status) {
    case QC_REPORTS.STATUSES.IN_PROGRESS:
      description = DESCRIPTIONS.IN_PROGRESS
      break
    case QC_REPORTS.STATUSES.ERRORS:
      description = `${DESCRIPTIONS.COMPLETE} ${DESCRIPTIONS.ERRORS[role as keyof object] || ''}`
      if (displayNoteToTitle) description = `${description} ${DESCRIPTIONS.NOTE_TO_TITLE}`
      break
    case QC_REPORTS.STATUSES.PASSED:
      description = `${DESCRIPTIONS.COMPLETE} ${DESCRIPTIONS.PASSED}`
      break
  }

  const statusIcon = STATUS_ICONS[status]
  const StatusIcon = statusIcon?.icon

  const isFailedStatus = useMemo(
    () => status === QC_REPORTS.STATUSES.FAILED || status === QC_REPORTS.STATUSES.CANCELLED,
    [status]
  )

  if (!inProgress && !completedAt) return null
  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h3 className="panel-title">Quality Control</h3>
      </div>
      <div className="panel-body">
        <div style={{ marginBottom: 12 }}>
          <Typography.Text>{description}</Typography.Text>
        </div>

        {isFailedStatus && (
          <Alert
            message={<strong>Could not QC</strong>}
            description={
              <>
                We could not complete the title QC review.
                {statusReason && (
                  <>
                    {' Please see details below:'}
                    <br />
                    {statusReason}
                  </>
                )}
              </>
            }
            type="warning"
            icon={<InfoCircleFilled />}
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}

        {displayPackageError && (
          <Alert
            message={<strong>Package error</strong>}
            description={packageError}
            type="error"
            icon={<CloseCircleFilled />}
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}
        {displayNoteToTitle && (
          <Alert
            message={<strong>Title review required</strong>}
            description={noteToTitle}
            type="info"
            icon={<InfoCircleFilled />}
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}

        {notaryAcknowledgementEnabled && <NotaryAcknowledgementAlerts notaryAcknowledgement={notaryAcknowledgement} />}

        {errors && <ErrorsTable errors={documentErrors} hasAutomatedReview orderId={orderId} />}

        {notaryAcknowledgementEnabled && (
          <NotaryAcknowledgementActions
            notaryAcknowledgement={notaryAcknowledgement}
            submittingAck={submittingAck}
            onSubmit={submitAcknowledgement}
          />
        )}

        {!errors && !isFailedStatus && (
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <StatusIcon className={statusIcon.color} style={{ fontSize: 42 }} />
            <Typography.Text style={{ fontSize: 20 }}>{statusIcon.label}</Typography.Text>
            {shouldDisplayQCLink && reviewUrl && (
              <Button
                type="primary"
                href={reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: 16 }}
              >
                Start QC
              </Button>
            )}
          </Space>
        )}
      </div>
    </div>
  )
}

const WithProvider: FC<TitleQCReportProps> = (props): ReactElement => (
  <Provider store={store}>
    <TitleQCReport {...props} />
  </Provider>
)

export default WithProvider
